/**
 * Bereitet eine hochgeladene Unterschrift automatisch auf:
 *  - schneidet den umgebenden Weißraum weg (Auto-Crop auf die "Tinte")
 *  - macht (nahezu) weiße Pixel transparent
 *  - gibt ein zugeschnittenes PNG (Data-URL) zurück
 *
 * Läuft komplett im Browser (Canvas). Bei Fehlern wird das Originalbild
 * unverändert zurückgegeben.
 *
 * @param {string} dataUrl  Bild als Data-URL (z.B. aus FileReader)
 * @param {object} opts     { padding, threshold }
 * @returns {Promise<string>} aufbereitete Data-URL (PNG)
 */
export function processSignatureImage(dataUrl, { padding = 16, threshold = 230 } = {}) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const cv = document.createElement("canvas");
        cv.width = img.width;
        cv.height = img.height;
        const ctx = cv.getContext("2d");
        ctx.drawImage(img, 0, 0);

        const full = ctx.getImageData(0, 0, cv.width, cv.height);
        const px = full.data;

        let minX = cv.width, minY = cv.height, maxX = 0, maxY = 0, found = false;
        for (let y = 0; y < cv.height; y++) {
          for (let x = 0; x < cv.width; x++) {
            const i = (y * cv.width + x) * 4;
            const r = px[i], g = px[i + 1], b = px[i + 2], a = px[i + 3];
            const isInk = a > 20 && (r < threshold || g < threshold || b < threshold);
            if (isInk) {
              found = true;
              if (x < minX) minX = x;
              if (x > maxX) maxX = x;
              if (y < minY) minY = y;
              if (y > maxY) maxY = y;
            }
          }
        }

        if (!found) {
          resolve(dataUrl);
          return;
        }

        minX = Math.max(0, minX - padding);
        minY = Math.max(0, minY - padding);
        maxX = Math.min(cv.width - 1, maxX + padding);
        maxY = Math.min(cv.height - 1, maxY + padding);
        const w = maxX - minX + 1;
        const h = maxY - minY + 1;

        const cropped = ctx.getImageData(minX, minY, w, h);
        const cp = cropped.data;
        // (nahezu) weiße Pixel transparent machen
        for (let i = 0; i < cp.length; i += 4) {
          if (cp[i] >= threshold && cp[i + 1] >= threshold && cp[i + 2] >= threshold) {
            cp[i + 3] = 0;
          }
        }

        const out = document.createElement("canvas");
        out.width = w;
        out.height = h;
        out.getContext("2d").putImageData(cropped, 0, 0);
        resolve(out.toDataURL("image/png"));
      } catch {
        resolve(dataUrl);
      }
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}
